/**
 * Motor comprehensive chargeable benefits — shared customer/agent patterns.
 */

import axios from 'axios';
import {
  MOTOR_COVER_COMPREHENSIVE,
  MOTOR_PRODUCT_GROUP,
} from '@/constants/motorPrivate';

export function computeUntaxedBasicPremium(totalPremium) {
  const t = Number(totalPremium) || 0;
  if (!t) return 0;
  return Math.round((t - 40) / 1.0045);
}

export async function fetchMotorChargeableBenefits(covertypeId = MOTOR_COVER_COMPREHENSIVE) {
  try {
    const res = await axios.get(
      `/customer/gis/products/${MOTOR_PRODUCT_GROUP}/covertypes/${covertypeId}/chargeablebenefits`,
      { _skipErrorRedirect: true },
    );
    return { success: true, data: res.data?.object || [] };
  } catch (e) {
    return {
      success: false,
      message: e?.response?.data?.message || 'Failed to load benefits',
      data: [],
    };
  }
}

/** Normalize sect codes so "18193" and 18193 match (restore vs GIS payload). */
function addonCodeSet(selectedAddons) {
  return new Set((selectedAddons || []).map((c) => String(c)));
}

function limitFor(customLimits, sectCode) {
  if (!customLimits) return null;
  const v = customLimits[sectCode] ?? customLimits[String(sectCode)];
  return v || null;
}

/**
 * keepFreeLimit: renewals must keep free-limit sections already on the policy,
 * otherwise benefit/change (full replace) deletes them from the renewal risk.
 */
export function mapBenefitsForChangeApi(
  chargeableBenefits,
  selectedAddons,
  customLimits = {},
  { keepFreeLimit = false } = {},
) {
  const selected = addonCodeSet(selectedAddons);
  return (chargeableBenefits || [])
    .filter((b) => {
      if (!selected.has(String(b.sectCode))) return false;
      if (b.freeLimit > 0 && !keepFreeLimit) {
        const custom = limitFor(customLimits, b.sectCode);
        return custom && custom > b.freeLimit;
      }
      return true;
    })
    .map((b) => ({
      noOfBeneficiaries: b.noOfBeneficiaries || 0,
      sectionCode: b.sectCode,
      sectionName: b.sectDesc,
      // Backend BenefitDto only reads "value"; null defaults to free limit / car value.
      value: limitFor(customLimits, b.sectCode)
        || (keepFreeLimit && b.freeLimit > 0 ? b.freeLimit : null),
    }));
}

export function mapBenefitsForQuoteUpdate(chargeableBenefits, selectedAddons, customLimits = {}) {
  const selected = addonCodeSet(selectedAddons);
  return (chargeableBenefits || [])
    .filter((b) => selected.has(String(b.sectCode)))
    .map((b) => ({
      id: b.sectCode,
      benefit: b.sectDesc,
      value: limitFor(customLimits, b.sectCode),
      noOfBeneficiaries: b.noOfBeneficiaries || 0,
    }));
}

export async function syncMotorQuoteBenefits(
  riskId,
  chargeableBenefits,
  selectedAddons,
  customLimits,
  options = {},
) {
  const benefits = mapBenefitsForChangeApi(chargeableBenefits, selectedAddons, customLimits, options);
  // benefit/change replaces the full set. Never POST [] while the UI still has
  // selected add-ons — that wipes risk_chargeable_benefits (seen on quote 13519).
  if ((selectedAddons || []).length > 0 && benefits.length === 0) {
    return {
      success: false,
      message: 'Selected benefits could not be mapped for sync',
      skipped: true,
    };
  }
  try {
    const res = await axios.post(
      '/customer/quotation/benefit/change',
      { riskId, benefits },
      { _skipErrorRedirect: true },
    );
    const data = res.data?.data || res.data?.object || res.data;
    return { success: true, data };
  } catch (e) {
    return {
      success: false,
      message: e?.response?.data?.message || 'Failed to update benefits',
    };
  }
}

export function buildBenefitPremiumMap(benefitDetails) {
  return (benefitDetails || []).reduce((map, b) => {
    const code = b.sectCode || b.sectionCode;
    if (code) return { ...map, [code]: b };
    return map;
  }, {});
}

/** GIS motor SI section — not an add-on the client can toggle. */
export const MOTOR_SUM_INSURED_SECT_CODE = 18182;

function sectionName(section) {
  return String(section?.pilDesc || section?.pilSectShtDesc || section?.sectDesc || '').trim();
}

function isSumInsuredSection(section) {
  const code = Number(section?.pilSectCode ?? section?.sectCode);
  if (code === MOTOR_SUM_INSURED_SECT_CODE) return true;
  const name = sectionName(section).toUpperCase().replace(/\s+/g, ' ');
  return /SUM\s*INSURED/.test(name);
}

/** Policy sections from GIS renewal batch detail, excluding sum insured. */
export function formatRenewalBenefitName(name) {
  const raw = String(name || '').replace(/^SECTION\s+/i, '').trim();
  if (!raw) return '';
  return raw
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function renewalBenefitLimitLabel(row, formatCurrency) {
  const free = Number(row?.freeLimitAmt) || 0;
  const limit = Number(row?.limitAmt) || 0;
  if (free > 0 && (!limit || limit <= free)) {
    return `Free up to ${formatCurrency(free)}`;
  }
  if (limit > 0) return formatCurrency(limit);
  return '';
}

export function displayRenewalBenefitRows(risks = []) {
  const rows = [];
  const seen = new Set();
  (risks || []).forEach((risk) => {
    (Array.isArray(risk?.sections) ? risk.sections : []).forEach((section) => {
      if (isSumInsuredSection(section)) return;
      const code = Number(section.pilSectCode ?? section.sectCode);
      const name = sectionName(section);
      if (!name && !Number.isFinite(code)) return;
      const key = Number.isFinite(code)
        ? String(code)
        : name.toUpperCase().replace(/\s+/g, ' ');
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({
        riskLabel: risk.riskId || risk.riskDesc || 'Vehicle',
        sectCode: Number.isFinite(code) ? code : null,
        name: name || `Section ${code}`,
        limitAmt: Number(section.pilLimitAmt) || 0,
        freeLimitAmt: Number(section.pilFreeLimitAmt) || 0,
        premAmt: Number(section.pilPremAmt) || 0,
      });
    });
  });
  return rows;
}

export function gisSectionsToAdditionalBenefits(risks = []) {
  return displayRenewalBenefitRows(risks)
    .filter((row) => row.sectCode != null)
    .map((row) => ({
      id: row.sectCode,
      sectCode: row.sectCode,
      sectionCode: row.sectCode,
      benefit: row.name,
      value: row.limitAmt || row.freeLimitAmt || null,
      insuredAmount: row.limitAmt || null,
    }));
}

/** Windscreen and infotainment are given with the cover; back office sets their free limit per policy. */
export function isFreeCoverBenefitName(name) {
  const raw = String(name || '').toUpperCase();
  const compact = raw.replace(/[^A-Z]/g, '');
  return compact.includes('WINDSCREEN')
    || raw.includes('ENTERTAINMENT')
    || raw.includes('INFORTAINMENT')
    || raw.includes('INFOTAINMENT')
    || /RADIO[\s-]?CASSETT/.test(raw)
    || raw.includes('EXTRA RADIO');
}

/** Underwritten GIS sections by sect code; their premium is already inside the renewal notice premium. */
export function renewalNoticeSections(risks = []) {
  const byCode = {};
  (risks || []).forEach((risk) => {
    (Array.isArray(risk?.sections) ? risk.sections : []).forEach((section) => {
      const code = Number(section?.pilSectCode);
      if (!Number.isFinite(code) || isSumInsuredSection(section) || byCode[code]) return;
      byCode[code] = {
        limit: Number(section.pilLimitAmt) || Number(section.pilFreeLimitAmt) || 0,
        rate: Number(section.pilPremRate) || 0,
        divFactor: Number(section.pilRateDivFact) || 0,
        premium: Number(section.pilPremAmt) || 0,
        locked: [section.pilSectShtDesc, section.pilDesc].some(isFreeCoverBenefitName),
      };
    });
  });
  return byCode;
}

function normalizeReg(value) {
  return String(value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** One quote risk per vehicle from the renewal start response (older responses only carry riskId). */
export function renewalQuoteVehicles(data, noticeRisks = []) {
  const listed = (Array.isArray(data?.vehicleRisks) ? data.vehicleRisks : [])
    .filter((v) => v && v.riskId)
    .map((v) => ({ riskId: Number(v.riskId), regNo: v.regNo || '' }));
  if (listed.length) return listed;
  return data?.riskId ? [{ riskId: Number(data.riskId), regNo: noticeRisks?.[0]?.riskId || '' }] : [];
}

export function noticeRiskFor(noticeRisks, regNo, index = 0) {
  const risks = Array.isArray(noticeRisks) ? noticeRisks : [];
  const wanted = normalizeReg(regNo);
  return risks.find((r) => wanted && normalizeReg(r?.riskId) === wanted) || risks[index] || null;
}

/**
 * Limits sent for one vehicle: sections already on that vehicle keep its own underwritten limit unless the client
 * changed the policy-level limit shown in the UI (e.g. windscreen 50,000 on one car, 30,000 on the other).
 */
export function vehicleBenefitLimits(limits = {}, policySections = {}, vehicleNoticeRisk = null) {
  const own = renewalNoticeSections(vehicleNoticeRisk ? [vehicleNoticeRisk] : []);
  const next = { ...(limits || {}) };
  Object.entries(own).forEach(([code, section]) => {
    const chosen = Number(limitFor(limits, code)) || 0;
    const policyLimit = Number(policySections?.[code]?.limit) || 0;
    if (section.limit > 0 && (!chosen || chosen === policyLimit)) next[code] = section.limit;
  });
  return next;
}

/** Benefit premiums summed across vehicles, keyed as buildBenefitPremiumMap expects. */
export function mergeBenefitDetails(perVehicle = []) {
  const byCode = new Map();
  (perVehicle || []).forEach((benefits) => {
    (benefits || []).forEach((b) => {
      const code = b?.sectCode || b?.sectionCode;
      if (!code) return;
      const prev = byCode.get(String(code));
      if (!prev) {
        byCode.set(String(code), { ...b });
        return;
      }
      prev.benefitPremium = (Number(prev.benefitPremium) || 0) + (Number(b.benefitPremium) || 0);
    });
  });
  return [...byCode.values()];
}

export function renewalVehicleKey(risk, index = 0) {
  return normalizeReg(risk?.riskId) || `vehicle-${index + 1}`;
}

/** Vehicles on the renewal notice, each with its own underwritten sections. */
export function renewalBenefitVehicles(noticeRisks = []) {
  return (noticeRisks || []).map((risk, index) => ({
    key: renewalVehicleKey(risk, index),
    label: risk?.riskId || `Vehicle ${index + 1}`,
    value: Number(risk?.riskValue) || 0,
    noticeSections: renewalNoticeSections([risk]),
  }));
}

/** Benefits chosen on at least one vehicle, with the first chosen limit, for the renewal start request. */
export function policyWideSelection(selections = {}) {
  const addons = [];
  const limits = {};
  Object.values(selections || {}).forEach((selection) => {
    (selection?.addons || []).forEach((code) => {
      if (addons.some((c) => String(c) === String(code))) return;
      addons.push(code);
      const value = limitFor(selection.limits, code);
      if (value) limits[code] = value;
    });
  });
  return { addons, limits };
}

export function vehicleSelectionFor(selections, noticeRisks, vehicle, index = 0) {
  const risks = Array.isArray(noticeRisks) ? noticeRisks : [];
  const risk = noticeRiskFor(risks, vehicle?.regNo, index);
  const at = risks.indexOf(risk);
  return (selections || {})[renewalVehicleKey(risk, at < 0 ? index : at)] || null;
}

/** Applies each vehicle's own benefit choice to its risk; the renewal premium is the sum across vehicles. */
export async function syncRenewalVehicleBenefits(vehicles, noticeRisks, chargeableBenefits, selections) {
  const list = vehicles || [];
  const chosen = list.map((vehicle, index) => vehicleSelectionFor(selections, noticeRisks, vehicle, index));
  const unmatched = list.find((vehicle, index) => !chosen[index]);
  if (unmatched) {
    return { success: false, message: `Benefits for ${unmatched.regNo || 'a vehicle'} could not be matched` };
  }
  const results = await Promise.all(list.map((vehicle, index) => syncMotorQuoteBenefits(
    vehicle.riskId,
    chargeableBenefits,
    chosen[index].addons,
    chosen[index].limits,
    { keepFreeLimit: true },
  )));
  const failed = results.find((r) => !r.success);
  if (failed) return failed;
  const premiums = results.map((r, index) => ({
    riskId: list[index].riskId,
    regNo: list[index].regNo,
    premium: r.data?.totalPremium != null ? Number(r.data.totalPremium) : null,
  }));
  const totalPremium = premiums.some((v) => v.premium == null)
    ? null
    : premiums.reduce((sum, v) => sum + v.premium, 0);
  return {
    success: true,
    data: {
      totalPremium,
      benefits: mergeBenefitDetails(results.map((r) => r.data?.benefits || [])),
      vehicles: premiums,
    },
  };
}

/** Catalogue benefit given free with comprehensive cover (windscreen, infotainment) up to its free limit. */
export function isFreeCoverBenefit(benefit) {
  return Number(benefit?.freeLimit) > 0 && isFreeCoverBenefitName(benefit?.sectDesc);
}

/**
 * Windscreen/infotainment on the renewal notice stay on the renewal at no less than the underwritten limit, and are
 * added at their free limit when the notice lacks them (new business always includes them); fixes selections restored
 * from older saved journeys.
 */
export function enforceLockedRenewalBenefits(addons = [], limits = {}, chargeableBenefits = [], noticeSections = {}) {
  const nextAddons = [...(addons || [])];
  const nextLimits = { ...(limits || {}) };
  const selected = addonCodeSet(nextAddons);
  (chargeableBenefits || []).forEach((benefit) => {
    const section = noticeSections?.[benefit.sectCode] || noticeSections?.[String(benefit.sectCode)];
    let minLimit;
    if (section?.locked) minLimit = section.limit;
    else if (!section && isFreeCoverBenefit(benefit)) minLimit = Number(benefit.freeLimit);
    else return;
    if (!selected.has(String(benefit.sectCode))) {
      nextAddons.push(benefit.sectCode);
      selected.add(String(benefit.sectCode));
    }
    if (minLimit > 0 && (Number(limitFor(nextLimits, benefit.sectCode)) || 0) < minLimit) {
      nextLimits[benefit.sectCode] = minLimit;
    }
  });
  return { addons: nextAddons, limits: nextLimits };
}

const RENEWAL_LEVY_FACTOR = 1.0045;

/**
 * Premium (incl. levies) a limit change adds to the renewal notice premium: only the change in limit at the
 * notice section rate, never taking off more than the section costs. Mirrors quotations RenewalNoticePremiumService.
 */
export function renewalLimitChangePremium(section, newLimit) {
  const limit = Number(section?.limit) || 0;
  const rate = Number(section?.rate) || 0;
  const divFactor = Number(section?.divFactor) || 0;
  const wanted = Number(newLimit) || 0;
  if (!wanted || !limit || !rate || divFactor <= 0 || wanted === limit) return 0;
  const net = Math.max(((wanted - limit) * rate) / divFactor, -(Number(section.premium) || 0));
  if (!net) return 0;
  // Round to cents first so float noise (2000 * 1.0045 = 2009.0000000000002) doesn't ceil up a shilling.
  const withLevy = Math.round(Math.abs(net) * RENEWAL_LEVY_FACTOR * 100) / 100;
  return Math.sign(net) * Math.ceil(withLevy);
}

export function restoreBenefitSelectionFromRisk(additionalBenefits) {
  const addons = [];
  const limits = {};
  (additionalBenefits || []).forEach((b) => {
    const raw = b.id || b.sectionCode || b.sectCode;
    if (raw == null || raw === '') return;
    // Prefer numeric codes to match GIS sectCode / BenefitLimitSelector.
    const asNum = Number(raw);
    const code = Number.isFinite(asNum) ? asNum : raw;
    addons.push(code);
    const val = b.value || b.insuredAmount;
    if (val) limits[code] = Number(val);
  });
  return { addons, limits };
}

/** Tick catalogue add-ons that already exist on the GIS policy. */
export function restoreRenewalAddons(risks, chargeableBenefits = []) {
  const restored = restoreBenefitSelectionFromRisk(gisSectionsToAdditionalBenefits(risks));
  if (!chargeableBenefits.length) return restored;
  const byCode = new Map((chargeableBenefits || []).map((b) => [String(b.sectCode), b.sectCode]));
  const addons = restored.addons
    .filter((code) => byCode.has(String(code)))
    .map((code) => byCode.get(String(code)));
  const limits = {};
  addons.forEach((code) => {
    const value = restored.limits[code] ?? restored.limits[String(code)];
    if (value) limits[code] = value;
  });
  return { addons, limits };
}

/** Locked notice benefits stay on each vehicle at no less than that vehicle's own underwritten limit. */
export function enforceLockedVehicleSelections(selections = {}, noticeRisks = [], chargeableBenefits = []) {
  return renewalBenefitVehicles(noticeRisks).reduce((acc, vehicle) => {
    const current = (selections || {})[vehicle.key] || { addons: [], limits: {} };
    return {
      ...acc,
      [vehicle.key]: enforceLockedRenewalBenefits(
        current.addons,
        current.limits,
        chargeableBenefits,
        vehicle.noticeSections,
      ),
    };
  }, {});
}

/**
 * Benefit choice per vehicle: what is on that vehicle's notice, or a saved policy-wide choice (older journeys)
 * applied to every vehicle on its own limits.
 */
export function initialVehicleSelections(noticeRisks = [], chargeableBenefits = [], policyWide = null) {
  const policySections = renewalNoticeSections(noticeRisks);
  const selections = (noticeRisks || []).reduce((acc, risk, index) => ({
    ...acc,
    [renewalVehicleKey(risk, index)]: policyWide
      ? {
        addons: [...(policyWide.addons || [])],
        limits: vehicleBenefitLimits(policyWide.limits, policySections, risk),
      }
      : restoreRenewalAddons([risk], chargeableBenefits),
  }), {});
  return enforceLockedVehicleSelections(selections, noticeRisks, chargeableBenefits);
}

export function buildAdditionalBenefitsSnapshot(
  chargeableBenefits,
  selectedAddons,
  customLimits,
  benefitDetails = [],
) {
  return mapBenefitsForQuoteUpdate(chargeableBenefits, selectedAddons, customLimits).map((b) => {
    const detail = (benefitDetails || []).find(
      (d) => String(d.sectionCode || d.sectCode) === String(b.id),
    );
    return {
      ...b,
      sectionCode: b.id,
      sectCode: b.id,
      sectionName: b.benefit,
      premium: detail?.benefitPremium ?? detail?.premium ?? 0,
      insuredAmount: limitFor(customLimits, b.id) || detail?.insuredAmount || 0,
    };
  });
}

export function applyBenefitSyncToQuote(
  quote,
  riskId,
  syncData,
  chargeableBenefits,
  selectedAddons,
  customLimits,
) {
  if (!quote?.covertypes) return quote;
  const additionalBenefits = buildAdditionalBenefitsSnapshot(
    chargeableBenefits,
    selectedAddons,
    customLimits,
    syncData?.benefits,
  );
  const covertypes = quote.covertypes.map((ct) => {
    if (Number(ct.riskId) !== Number(riskId)) return ct;
    const updated = { ...ct, additionalBenefits };
    if (syncData?.totalPremium != null && updated.riskPremDto) {
      updated.riskPremDto = {
        ...updated.riskPremDto,
        totalPremium: syncData.totalPremium,
      };
    }
    return updated;
  });
  return { ...quote, covertypes };
}

export function applyLocalBenefitsToQuote(
  quote,
  riskId,
  chargeableBenefits,
  selectedAddons,
  customLimits,
) {
  if (!quote?.covertypes || !selectedAddons?.length) return quote;
  const additionalBenefits = buildAdditionalBenefitsSnapshot(
    chargeableBenefits,
    selectedAddons,
    customLimits,
    [],
  );
  const covertypes = quote.covertypes.map((ct) => (
    Number(ct.riskId) === Number(riskId)
      ? { ...ct, additionalBenefits }
      : ct
  ));
  return { ...quote, covertypes };
}
